import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, FileSignature, Info, Loader2, PenLine, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

import { cn } from '../../../lib/utils';
import { signedSaApiFor, useSignedSa, useSignedSas } from '../../../services/clientOnboarding';
import { AgreementSection, FactsSection } from '../intake/signup/signupSections';

// Signed SAs (Nic 2026-10-10): every client with a signed agreement on
// record. Something on one is wrong (rating, married, dependents)? Correct
// the facts and send a new one for them to sign, right here. The claim never
// moves: no send back, no disruption to a CM already working it. Paged on
// the server, searched by the page's search box, filtered by where the
// claim is now. Anyone on the onboarding team corrects anyone.

// Chips, in order. dot = the small color mark on the chip and the row.
const STAGES = [
  { key: 'active', label: 'Active', hint: 'Onboarding or with a CM', dot: 'bg-sky-500' },
  { key: 'onboarding', label: 'In onboarding', dot: 'bg-indigo-400' },
  { key: 'with_cm', label: 'With a CM', dot: 'bg-teal-400' },
  { key: 'billing', label: 'Billing', dot: 'bg-amber-400' },
  { key: 'paid', label: 'Paid', dot: 'bg-emerald-500' },
  { key: 'dead', label: 'Dead', dot: 'bg-slate-400' },
  // Their claim was deleted on the claims board (Nic 2026-10-23: never
  // really no claim; often a duplicate account's).
  { key: 'no_claim', label: 'Claim deleted', dot: 'bg-slate-300' },
  { key: 'correcting', label: 'Correction open', dot: 'bg-orange-500' },
];
const STAGE_BY_KEY = Object.fromEntries(STAGES.map((s) => [s.key, s]));
// A specialist's chips (Nic 2026-10-27): only the clients still in play. The
// server lists them nothing past With a CM (`allStages` false), so no All
// chip either: Active is their everything. Billing, Paid, Dead and Claim
// deleted are the Onboarding Manager's and admins'.
const SPECIALIST_CHIPS = ['active', 'onboarding', 'with_cm', 'correcting'];

const STATUS_LABEL = {
  intake: 'Intake',
  'awaiting medical records': 'Awaiting Med. Recs.',
  'in process': 'In Process',
  'awaiting internal review': 'Awaiting Review',
  'internal review approved': 'Review Approved',
  'submitted to va': 'Submitted',
  invoiced: 'Invoiced',
  'payment plan': 'Payment Plan',
  'dispute hold': 'Dispute Hold',
  paid: 'Paid',
  dead: 'Dead',
};

// In onboarding, the claim's legacy Intake / Awaiting Med. Recs. words never
// show (Nic 2026-10-27): the row reads "Onboarding" and where they are, the
// same stage names as the Clients tab.
const ONBOARDING_STAGE = {
  vaGov: 'Setting up VA.gov',
  beforeCall: 'Before the first call',
  records: 'Gathering records',
  readyForCm: 'Ready for CM',
  stalled: 'Stalled',
};
const claimLabelOf = (r) => {
  if (r.stage === 'onboarding' && r.onboarding) return { main: 'Onboarding', sub: ONBOARDING_STAGE[r.onboarding.column] || null };
  return { main: r.claimStatus ? STATUS_LABEL[r.claimStatus] || r.claimStatus : 'Claim deleted', sub: null };
};

const initialsOf = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('') || '?';

const GRID =
  'lg:grid lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_4.5rem_minmax(0,1fr)_minmax(0,1fr)_9rem] lg:items-center lg:gap-x-6';
// Pixel padding on purpose: px-4 / px-5 are also Bootstrap classes.
const PAD_X = 'px-[16px] lg:px-[20px]';

const Chip = ({ active, onClick, dot, label, count, title }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    title={title}
    className={cn(
      'inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold outline-none focus:outline-none',
      active
        ? 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900'
        : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10',
    )}
  >
    {dot ? <span className={cn('size-1.5 rounded-full', dot)} /> : null}
    {label}
    <span className={cn('tabular-nums', active ? 'opacity-70' : 'text-slate-400 dark:text-slate-500')}>
      {count}
    </span>
  </button>
);

// Their signed agreement PDF in a new tab (Nic 2026-10-23).
export const openSignedPdf = async (pdf) => {
  try {
    const res = await axios.get(`/claims/${pdf.claimId}/files/${pdf.fileId}/read`);
    if (res.data?.url) window.open(res.data.url, '_blank', 'noopener');
  } catch (e) {
    toast.error(e?.response?.data?.msg || "That PDF couldn't be opened.");
  }
};

const Row = ({ r, onOpen, canAct }) => {
  const st = STAGE_BY_KEY[r.stage];
  const signed = r.lastSignedAt ? moment(r.lastSignedAt) : null;
  const claim = claimLabelOf(r);
  return (
    <li
      className={cn(
        'group flex cursor-pointer flex-col gap-2 py-3 hover:bg-slate-50/80 dark:hover:!bg-white/[0.03]',
        PAD_X,
        GRID,
      )}
      onClick={() => onOpen(r)}
    >
      {/* Client */}
      <div className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-600 dark:!bg-white/10 dark:text-slate-300"
        >
          {initialsOf(r.name)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{r.name}</div>
          {r.correcting ? (
            <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-orange-100 px-1.5 text-[10.5px] font-semibold leading-5 text-orange-800 dark:!bg-orange-500/15 dark:text-orange-300">
              <PenLine size={10} /> Correction open
            </span>
          ) : null}
        </div>
      </div>

      {/* Signed */}
      <div className="flex min-w-0 items-baseline gap-2 pl-11 lg:block lg:pl-0">
        <div className="whitespace-nowrap text-[13px] text-slate-700 dark:text-slate-200">
          {signed ? signed.format('MMM D, YYYY') : '-'}
        </div>
        <div className="text-[11px] text-slate-400 dark:text-slate-500">
          {r.resigned ? 'Re-signed' : signed ? signed.fromNow() : ''}
        </div>
      </div>

      {/* Rating */}
      <div className="hidden text-[13px] font-semibold tabular-nums text-slate-700 dark:text-slate-200 lg:block">
        {r.rating != null ? `${r.rating}%` : '-'}
      </div>

      {/* Claim */}
      <div className="flex min-w-0 items-center gap-1.5 pl-11 lg:pl-0">
        {st ? <span className={cn('size-1.5 shrink-0 rounded-full', st.dot)} /> : null}
        <div className="min-w-0">
          <div className="truncate text-[13px] text-slate-700 dark:text-slate-200">{claim.main}</div>
          {claim.sub ? <div className="truncate text-[11px] text-slate-400 dark:text-slate-500">{claim.sub}</div> : null}
        </div>
      </div>

      {/* With */}
      <div className="hidden min-w-0 lg:block">
        {r.withName ? (
          <>
            <div className="truncate text-[13px] text-slate-700 dark:text-slate-200">{r.withName}</div>
            <div className="text-[11px] text-slate-400 dark:text-slate-500">
              {r.withRole === 'cm' ? 'Claims Manager' : 'Specialist'}
            </div>
          </>
        ) : (
          <span className="text-[13px] italic text-slate-400 dark:text-slate-500">Nobody</span>
        )}
      </div>

      {/* Action */}
      <div className="flex pl-11 lg:justify-end lg:pl-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(r);
          }}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border-[1px] px-3 text-xs font-semibold outline-none focus:outline-none',
            'border-slate-200 !bg-white !text-slate-700 hover:!border-slate-900 hover:!bg-slate-900 hover:!text-white group-hover:!border-slate-900 group-hover:!bg-slate-900 group-hover:!text-white',
            'dark:!border-white/15 dark:!bg-transparent dark:!text-slate-200 dark:hover:!border-slate-100 dark:hover:!bg-slate-100 dark:hover:!text-slate-900 dark:group-hover:!border-slate-100 dark:group-hover:!bg-slate-100 dark:group-hover:!text-slate-900',
          )}
        >
          <FileSignature size={13} /> See details
        </button>
      </div>
    </li>
  );
};

// One client's agreement: the same Agreement section as the Workspace, and
// the facts once a correction is under way (Correct & Resend opens them).
const AgreementDrawer = ({ person, onClose }) => {
  const q = useSignedSa(person.userId);
  const queryClient = useQueryClient();
  const [factsDirty, setFactsDirty] = useState(false);
  const api = useMemo(() => signedSaApiFor(person.userId), [person.userId]);
  const refresh = () => {
    queryClient.invalidateQueries(['client-onboarding', 'signed-sa', person.userId]);
    queryClient.invalidateQueries(['client-onboarding', 'signed-sas']);
  };
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const row = q.data?.row;
  const canAct = !!q.data?.canAct;
  return createPortal(
    <div className="fixed inset-0 z-[1055] flex justify-end">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px] outline-none"
      />
      <aside className="os-scroll relative flex h-full w-full max-w-[620px] flex-col overflow-y-auto bg-[#F5F7FA] shadow-2xl dark:!bg-[#0E121B]">
        <header className="sticky top-0 z-10 flex items-center gap-3 border-b-[1px] border-slate-200 bg-white px-[20px] py-3.5 dark:!border-white/10 dark:!bg-surface-card">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white dark:!bg-slate-100 dark:!text-slate-900">
            {initialsOf(person.name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-bold text-slate-800 dark:text-slate-100">{person.name}</div>
            <div className="truncate text-xs text-slate-500 dark:text-slate-400">
              {claimLabelOf(person).main}
              {claimLabelOf(person).sub ? ` · ${claimLabelOf(person).sub}` : ''}
              {person.withName ? ` · ${person.withName}` : ''}
            </div>
          </div>
          {/* No way to their claim from here (Nic 2026-10-12). */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:outline-none dark:hover:!bg-white/10 dark:hover:text-white"
          >
            <X size={18} />
          </button>
        </header>
        <div className="space-y-[12px] p-[20px]">
          {q.data?.lockedWhy ? (
            // Only up to With a CM (Nic 2026-10-23).
            <p className="mb-0 rounded-xl bg-sky-50 px-3 py-2.5 text-xs font-medium text-sky-800 ring-1 ring-sky-200 dark:!bg-sky-500/10 dark:text-sky-200 dark:ring-sky-500/30">
              {q.data.lockedWhy}
            </p>
          ) : (
            <p className="mb-0 text-xs text-slate-500 dark:text-slate-400">
              Something on their signed agreement needs changing? Correct the facts and send a new one for them to sign.
              The one they signed stands until they sign the new one, and their claim stays where it is.
            </p>
          )}
          {q.isLoading ? (
            <Loader2 size={18} className="animate-spin text-slate-400" />
          ) : !row ? (
            <div className="text-sm text-slate-400">{q.error?.response?.data?.msg || 'No agreement details on file.'}</div>
          ) : (
            <>
              {row.userIntakeCorrectionStartedAt ? (
                <FactsSection
                  key={`facts-${row.userId}-${row.userIntakeConfirmedAt || 'none'}-${row.userIntakeCorrectionStartedAt || ''}`}
                  api={api}
                  row={row}
                  claimedByMe={canAct}
                  onConfirmed={refresh}
                  onDirtyChange={setFactsDirty}
                  onboarding
                />
              ) : null}
              <AgreementSection
                api={api}
                row={row}
                claimedByMe={canAct}
                factsDirty={factsDirty}
                onChanged={refresh}
                signedPdf={person.pdf || null}
                onOpenSignedPdf={openSignedPdf}
              />
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

// q is the page's one search (the header box), already debounced. onCounts
// tells the page how many the search shows of how many the tab has (its
// "Filtered by" bar, Nic 2026-10-27); null while the count loads.
const SignedSasView = ({ q = '', onCounts = null }) => {
  const [page, setPage] = useState(1);
  const [stage, setStage] = useState('active');
  const [open, setOpen] = useState(null);
  const list = useSignedSas({ page, q, stage });

  // A new search or filter starts at page 1.
  useEffect(() => setPage(1), [q, stage]);

  const rows = list.data?.rows || [];
  const total = list.data?.total || 0;
  const unfiltered = list.data?.unfiltered;
  const loaded = !!list.data && !list.isFetching;
  useEffect(() => {
    if (!onCounts) return undefined;
    onCounts(q ? { shown: loaded ? total : null, total: unfiltered ?? 0 } : null);
    return () => onCounts(null);
  }, [onCounts, q, loaded, total, unfiltered]);
  const counts = list.data?.counts || {};
  const pageSize = list.data?.pageSize || 10;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, page * pageSize);
  const canAct = !!list.data?.canAct;
  const allStages = list.data?.allStages === true;
  // A specialist has no All: unpicking a chip lands back on Active.
  const picked = allStages ? stage : stage || 'active';

  // Chips with nobody in them stay out of the way, unless picked.
  const chips = STAGES.filter(
    (s) => (allStages || SPECIALIST_CHIPS.includes(s.key)) && (counts[s.key] > 0 || picked === s.key),
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="os-scroll -mx-1 flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto px-1 py-0.5">
          {allStages ? (
            <Chip active={!stage} onClick={() => setStage('')} label="All" count={counts.all ?? total} />
          ) : null}
          {chips.map((s) => (
            <Chip
              key={s.key}
              active={picked === s.key}
              onClick={() => setStage(picked === s.key ? '' : s.key)}
              label={s.label}
              title={s.hint}
              count={counts[s.key] || 0}
              dot={s.dot}
            />
          ))}
        </div>
      </div>

      <div className="mb-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <Info size={13} className="shrink-0 text-slate-400" />
          Correct a signed agreement here and send a new one. Their claim stays where it is.
        </span>
        <div className="ml-auto flex items-center gap-1">
          <span className="whitespace-nowrap tabular-nums">
            {total ? (
              <>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {from}-{to}
                </span>{' '}
                of {total}
              </>
            ) : (
              '0'
            )}
          </span>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
            className="rounded-lg p-1 text-slate-500 outline-none hover:bg-slate-200/60 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            aria-label="Next page"
            className="rounded-lg p-1 text-slate-500 outline-none hover:bg-slate-200/60 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="os-scroll relative min-h-0 flex-1 overflow-y-auto rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
        <div
          className={cn(
            'sticky top-0 z-10 hidden border-b-[1px] border-slate-100 bg-white/95 py-2 text-[10.5px] font-bold uppercase tracking-wider text-slate-400 backdrop-blur dark:!border-white/10 dark:!bg-surface-card/95 dark:text-slate-500 lg:grid',
            PAD_X,
            GRID,
          )}
        >
          <div>Client</div>
          <div>Signed</div>
          <div>Rating</div>
          <div>Claim</div>
          <div>With</div>
          <div className="text-right">
            {list.isFetching && !list.isLoading ? <Loader2 size={12} className="ml-auto animate-spin" /> : null}
          </div>
        </div>

        {list.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 size={20} className="animate-spin text-slate-400" />
          </div>
        ) : list.isError ? (
          <div className="flex h-40 items-center justify-center text-sm font-semibold text-rose-500">
            {list.error?.response?.data?.msg || "Signed SAs couldn't be loaded."}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-1 text-sm text-slate-400">
            <div className="font-semibold text-slate-500 dark:text-slate-400">
              {q || stage ? 'Nobody matches.' : 'No signed agreements yet.'}
            </div>
            {stage ? (
              <button
                type="button"
                onClick={() => setStage('')}
                className="text-xs font-semibold text-sky-600 outline-none hover:underline focus:outline-none dark:text-sky-400"
              >
                Show all
              </button>
            ) : null}
          </div>
        ) : (
          <ul
            aria-label="Signed SAs"
            className={cn(
              'mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/[0.06]',
              list.isFetching && !list.isLoading && 'opacity-70',
            )}
          >
            {rows.map((r) => (
              <Row key={r.userId} r={r} onOpen={setOpen} canAct={canAct} />
            ))}
          </ul>
        )}
      </div>

      {open ? <AgreementDrawer person={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
};

export default SignedSasView;
